/**
 * Oct 20 founder addendum (L2 / L3 / L6) — client side of the departure / return cycle.
 *
 * Every act is the server's (SECURITY DEFINER RPCs, re-authorized and re-validated under the repair's locks):
 *   incomplete_pickup_preview     what "Picked Up — Work Incomplete" will do: paused sessions to end (L2), future days
 *                                 to withdraw (L3), days kept, blockers
 *   check_in_returned_vehicle     the customer brought it back: front-office custody, physical location recorded (L6)
 *   create_return_continuation    the authorized linked continuation for the remaining work
 *   repair_departure_history      the departure / return / continuation record (Command Center, Workboard)
 *   my_departure_released_reviews the signed-in technician's time reviews created by a departure
 * Unknown outcomes fail closed (a client too old for a new server answer reports a refusal, never a success).
 */
import { supabase } from '@/integrations/supabase/client';
import { parsePausedSessions, type DeparturePausedSession } from '@/utils/ticketPickupLifecycle';

const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase as unknown as { rpc: (f: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> })
    .rpc(fn, args);

export interface DepartureDay {
  ticketId: string;
  ticketNumber: number | null;
  scheduledDate: string | null;
  predictedMinutes: number | null;
  technicianName: string | null;
}

export interface IncompletePickupPreview {
  ok: boolean;
  blocker: string | null;
  multiDay: boolean;
  runningSessions: number;
  awaitingReviewSessions: number;
  pausedSessions: DeparturePausedSession[];
  mayReleasePausedWork: boolean;
  futureDaysToWithdraw: DepartureDay[];
  laterDaysWithWork: DepartureDay[];
  error: string | null;
}

const day = (d: any): DepartureDay => ({
  ticketId: String(d.ticket_id), ticketNumber: d.ticket_number ?? null, scheduledDate: d.scheduled_date ?? null,
  predictedMinutes: d.predicted_minutes ?? null, technicianName: d.technician_name ?? null,
});

export async function readIncompletePickupPreview(ticketId: string): Promise<IncompletePickupPreview> {
  // Read from a dialog effect: a thrown call fails closed like a returned error, never as an unhandled rejection.
  let data: unknown = null; let error: { message: string } | null = null;
  try { ({ data, error } = await rpc('incomplete_pickup_preview', { p_ticket_id: ticketId })); }
  catch (e) { error = { message: e instanceof Error ? e.message : String(e) }; }
  const r = (data ?? {}) as Record<string, any>;
  if (error || r.outcome !== 'PREVIEW') {
    return { ok: false, blocker: r.outcome ?? 'SUBMIT_FAILED', multiDay: false, runningSessions: 0, awaitingReviewSessions: 0,
      pausedSessions: [], mayReleasePausedWork: false, futureDaysToWithdraw: [], laterDaysWithWork: [],
      error: error?.message ?? null };
  }
  return {
    ok: true,
    blocker: r.blocker ?? null,
    multiDay: !!r.multi_day,
    runningSessions: Number(r.running_sessions ?? 0),
    awaitingReviewSessions: Number(r.awaiting_review_sessions ?? 0),
    pausedSessions: parsePausedSessions(r.paused_sessions),
    mayReleasePausedWork: !!r.may_release_paused_work,
    futureDaysToWithdraw: Array.isArray(r.future_days_to_withdraw) ? r.future_days_to_withdraw.map(day) : [],
    laterDaysWithWork: Array.isArray(r.later_days_with_work) ? r.later_days_with_work.map(day) : [],
    error: null,
  };
}

/** Plain words for a preview blocker (the act re-checks everything; this only explains before the person confirms). */
export function previewBlockerWords(blocker: string | null): string | null {
  switch (blocker) {
    case null: return null;
    case 'ACTIVE_WORK': return 'A technician is working on this vehicle right now. Running work is never ended from the front office — ask them to pause or stop first.';
    case 'TIMER_INTEGRITY_BLOCK': return 'This ticket has timer evidence that needs a person to look at it first.';
    case 'LATER_DAY_HAS_WORK': return 'A later day of this repair already has technician work. Record the pickup from that day.';
    case 'LOCATION_ON_OTHER_DAY': return 'The vehicle is recorded on another day of this repair. Record the pickup from that day.';
    case 'PAUSED_RELEASE_NOT_AUTHORIZED': return 'Technician work is paused here. Only a Service Advisor, Manager or Owner can end it with the pickup.';
    case 'ALREADY_PICKED_UP': return 'This vehicle is already recorded as picked up.';
    case 'DIAGNOSTIC_OUTCOME_OWED': return 'A paused diagnostic job still needs its outcome from the technician. They record it (Partial or Not reproduced are outcomes) or end their own work first.';
    default: return 'This vehicle cannot be recorded as picked up right now.';
  }
}

export type ReturnLocation =
  | { kind: 'PARKING_SPOT'; parkingSpotId: string }
  | { kind: 'ON_SITE_GENERAL' }
  | { kind: 'ON_SITE_UNKNOWN_EXACT' };

export interface ActAnswer { ok: boolean; outcome: string; message: string; data: Record<string, any> }

const RETURN_WORDS: Record<string, string> = {
  RETURNED: 'Vehicle checked back in. It is with the front office; schedule the remaining work when ready.',
  ALREADY_RETURNED: 'This vehicle was already checked back in.',
  NOT_AWAITING_RETURN: 'This repair has no open “Picked Up — Work Incomplete” to check back in.',
  LOCATION_REQUIRED: 'Say where the vehicle is: a parking spot, the general lot, or on site.',
  LOCATION_REFUSED: 'That place could not be used. Nothing was recorded — choose another.',
  RETURN_STATE_CONFLICT: 'The ticket is not in the state an incomplete pickup leaves it in; a person must reconcile it.',
  TICKET_NOT_AVAILABLE: 'Only the front office can check a returning vehicle in.',
  FAMILY_CHANGED_RETRY: 'The repair changed while checking in. Nothing was recorded — try again.',
};

export async function checkInReturnedVehicle(ticketId: string, where: ReturnLocation, note?: string | null): Promise<ActAnswer> {
  const { data, error } = await rpc('check_in_returned_vehicle', {
    p_ticket_id: ticketId, p_location_kind: where.kind,
    p_parking_spot_id: where.kind === 'PARKING_SPOT' ? where.parkingSpotId : null, p_note: note ?? null,
  });
  const r = (data ?? {}) as Record<string, any>;
  if (error) return { ok: false, outcome: 'SUBMIT_FAILED', message: 'The check-in could not be confirmed. Reload before trying again.', data: r };
  const outcome = String(r.outcome ?? 'SUBMIT_FAILED');
  return { ok: outcome === 'RETURNED' || outcome === 'ALREADY_RETURNED', outcome,
    message: RETURN_WORDS[outcome] ?? 'The check-in was not recorded.', data: r };
}

const CONTINUATION_WORDS: Record<string, string> = {
  CONTINUATION_CREATED: 'The remaining work is scheduled as a continuation of this repair. Hand it to the shop when ready.',
  ALREADY_LINKED: 'This repair already has its continuation.',
  AWAITING_RETURN: 'The customer still has the vehicle. Check it in first.',
  NOT_A_RETURNED_REPAIR: 'This repair has no incomplete pickup to continue.',
  SCHEDULE_REQUIRED: 'Choose when the remaining work starts and how long it should take.',
  START_IN_PAST: 'The continuation cannot start on a day that has passed.',
  TECHNICIAN_NOT_AVAILABLE: 'That technician cannot take this work. Choose another or leave it unassigned.',
  CONTINUATION_EXISTS: 'This repair already continues on a live day.',
  ROLLOVER_DECISION_PENDING: 'A rollover decision is already open for this repair today. Resolve it first.',
  RETURN_STATE_CONFLICT: 'The ticket is not in the state a returned repair is in; a person must reconcile it.',
  TICKET_NOT_AVAILABLE: 'Only the front office can schedule the remaining work.',
  FAMILY_CHANGED_RETRY: 'The repair changed while scheduling. Nothing was recorded — try again.',
};

export async function createReturnContinuation(args: {
  ticketId: string; startTime: string; predictedMinutes: number; technicianId?: string | null; note?: string | null;
}): Promise<ActAnswer> {
  const { data, error } = await rpc('create_return_continuation', {
    p_ticket_id: args.ticketId, p_start_time: args.startTime, p_predicted_minutes: args.predictedMinutes,
    p_technician_id: args.technicianId ?? null, p_note: args.note ?? null,
  });
  const r = (data ?? {}) as Record<string, any>;
  if (error) return { ok: false, outcome: 'SUBMIT_FAILED', message: 'Scheduling could not be confirmed. Reload before trying again.', data: r };
  const outcome = String(r.outcome ?? 'SUBMIT_FAILED');
  return { ok: outcome === 'CONTINUATION_CREATED' || outcome === 'ALREADY_LINKED', outcome,
    message: CONTINUATION_WORDS[outcome] ?? 'The continuation was not created.', data: r };
}

export interface DepartureRecord {
  incompletePickupId: string;
  departureTicketId: string;
  departureTicketNumber: number | null;
  reason: string;
  expectedReturnDate: string | null;
  pickedUpAt: string;
  recordedByName: string | null;
  returnedAt: string | null;
  returnedByName: string | null;
  continuationTicketId: string | null;
  continuationTicketNumber: number | null;
  releasedPausedWork: Array<{ technician_name: string | null; paused_at: string; pause_reason: string;
    measured_minutes: number; review_pending: boolean; diagnostic_outcomes_owed: unknown[] }>;
  withdrawnDays: Array<{ ticket_id: string; scheduled_date: string | null; predicted_minutes: number | null }>;
}

export type DepartureState = 'awaiting_return' | 'returned_awaiting_scheduling' | 'continuation_scheduled' | 'continuation_ended' | 'none';

export interface DepartureHistory { state: DepartureState; departures: DepartureRecord[] }

export function parseDepartureHistory(raw: unknown): DepartureHistory | null {
  const r = (raw ?? {}) as Record<string, any>;
  if (r.outcome !== 'OK') return null;
  return {
    state: (r.state ?? 'none') as DepartureState,
    departures: (Array.isArray(r.departures) ? r.departures : []).map((d: any) => ({
      incompletePickupId: String(d.incomplete_pickup_id),
      departureTicketId: String(d.departure_ticket_id),
      departureTicketNumber: d.departure_ticket_number ?? null,
      reason: String(d.reason ?? ''),
      expectedReturnDate: d.expected_return_date ?? null,
      pickedUpAt: String(d.picked_up_at),
      recordedByName: d.recorded_by_name ?? null,
      returnedAt: d.returned_at ?? null,
      returnedByName: d.returned_by_name ?? null,
      continuationTicketId: d.continuation_ticket_id ?? null,
      continuationTicketNumber: d.continuation_ticket_number ?? null,
      releasedPausedWork: Array.isArray(d.released_paused_work) ? d.released_paused_work : [],
      withdrawnDays: Array.isArray(d.withdrawn_days) ? d.withdrawn_days : [],
    })),
  };
}

export async function readDepartureHistory(ticketId: string): Promise<DepartureHistory | null> {
  const { data, error } = await rpc('repair_departure_history', { p_ticket_id: ticketId });
  if (error) throw new Error(error.message);
  return parseDepartureHistory(data);
}

export interface DepartureReleasedReview {
  workEpisodeId: string; ticketId: string; ticketNumber: number | null; customerName: string | null;
  releasedAt: string; releasedByName: string | null; releasedByRole: string | null; departureReason: string;
  measuredMinutes: number;
}

export async function readMyDepartureReleasedReviews(): Promise<DepartureReleasedReview[]> {
  let data: unknown; let error: unknown;
  try { ({ data, error } = (await rpc('my_departure_released_reviews', {})) ?? { data: null, error: null }); }
  catch { return []; }
  if (error || !Array.isArray(data)) return [];
  return (data as any[]).map((r) => ({
    workEpisodeId: String(r.work_episode_id), ticketId: String(r.ticket_id), ticketNumber: r.ticket_number ?? null,
    customerName: r.customer_name ?? null, releasedAt: String(r.released_at), releasedByName: r.released_by_name ?? null,
    releasedByRole: r.released_by_role ?? null, departureReason: String(r.departure_reason ?? ''),
    measuredMinutes: Number(r.measured_minutes ?? 0),
  }));
}
